import { Component, computed, inject, signal, OnInit, effect } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SharedModule } from '../../../../zentility/_shared/shared.module';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { SaEditData } from '../state/bill-ai-flow.models';
import { ServiceAddressMatchResult, LegalEntityMatchResult, MeterMatchResult, BillMatchResult } from '../../../models/bill-import.models';
import { MatchingService } from '../../../services/matching.service';
import { StateV4Service } from '../../../../zentility/_shared/_services/v4/state-v4.service';
import { take } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { formatBillPeriod } from '../../../utils/date-format.util';
import { MIN_DISPLAY_SCORE, getScoreClass } from '../../../utils/match-score.util';
import { cleanIdentifierName, genericUanLabel } from '../../../utils/bill-utils';
import { EntityMatchSelectorComponent } from '../entity-match-selector/entity-match-selector.component';
import { ScoreSelectComponent, ScoreSelectOption } from '../score-select/score-select.component';
import { ParentContextBannerComponent } from '../parent-context-banner/parent-context-banner.component';
import { ParentConflictResolverComponent } from '../parent-context-banner/parent-conflict-resolver.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MergeModalComponent } from '../../merge-modal/merge-modal.component';
import { SelectionActionBarComponent } from '../../selection-action-bar/selection-action-bar.component';
import { validateSaStep, SaValidationInput } from './bill-ai-flow-sa-step.validation';
import { StepValidationResult, ValidationSummary, buildFieldErrorMaps } from '../validation';
import { buildAddressGroupingKey } from '../../../utils/address-normalization.util';

/**
 * A pair of SA groupIds whose normalized address keys are identical and
 * which the user hasn't yet resolved (merged or dismissed). The pair key
 * (`sortedIds.join('|')`) is used by the dismissal set so we never re-prompt
 * for the same pair after the user has chosen "No, keep distinct".
 *
 * Identity (`addressKey`) uses `buildAddressGroupingKey` so we share the
 * same normalization rules the server-side import uses for SA bucketing
 * — the prompt's job is to collapse the "two-then-one" surprise that
 * shows up because import-time merging happens after the user has already
 * passed through the SA / Meter / Summary screens.
 */
export interface SaMergePromptPair {
  /** The SA the user just edited — primary "target" for merge if they confirm. */
  editedSaGroupId: string;
  /** The pre-existing SA that now matches. */
  matchedSaGroupId: string;
  /** Stable key (sorted ids joined) used by the dismissal set. */
  pairKey: string;
  /** Display string for the dialog body. */
  addressLabel: string;
}

/** Input shape for the pure pair-detection helper. */
export interface SaPairDetectionInput {
  /** Each SA with a non-empty street1+city, plus its pre-built normalized key. */
  saInputs: Array<{ groupId: string; addressKey: string; addressLabel: string }>;
  /** Pairs the user has already dismissed via "No, keep distinct". */
  dismissedPairs: Set<string>;
  /** Pair keys we already noticed on a previous tick — used to detect "newly identical". */
  lastSeenPairKeys: Set<string>;
  /** The most recently edited SA, used to pick which side of the pair is "edited". */
  lastEditedSaGroupId: string | null;
}

/** Output of the pure pair-detection helper. */
export interface SaPairDetectionResult {
  /**
   * Pairs that became identical on this tick — i.e., are currently identical
   * AND were NOT identical on the previous tick (lastSeenPairKeys) AND have
   * not been dismissed (dismissedPairs). Suitable for opening a prompt.
   */
  newPairs: SaMergePromptPair[];
  /** Every pair that is currently identical, regardless of dismissal/last-seen state. */
  currentPairKeys: Set<string>;
}

/**
 * Pure detection of identical-address SA pairs after edit.
 *
 * A pair is "new" when its key is in currentPairKeys but NOT in lastSeenPairKeys
 * AND NOT in dismissedPairs. Callers own the set mutations — this function is
 * intentionally read-only so it stays trivial to test.
 *
 * Returns `{ newPairs: [], currentPairKeys: new Set() }` when
 * lastEditedSaGroupId is null. The prompt's framing ("we found a matching
 * address") only makes sense after the user has edited something — pre-
 * existing duplicates from the imported bill data are surfaced by the
 * cross-LEN duplicate banner instead.
 */
export function detectNewIdenticalSaPairs(input: SaPairDetectionInput): SaPairDetectionResult {
  const { saInputs, dismissedPairs, lastSeenPairKeys, lastEditedSaGroupId } = input;

  if (lastEditedSaGroupId === null) {
    return { newPairs: [], currentPairKeys: new Set() };
  }

  // Bucket by address key.
  const byKey = new Map<string, Array<{ groupId: string; addressLabel: string }>>();
  for (const sa of saInputs) {
    if (!sa.addressKey) continue;
    const list = byKey.get(sa.addressKey) ?? [];
    list.push(sa);
    byKey.set(sa.addressKey, list);
  }

  const currentPairKeys = new Set<string>();
  const newPairs: SaMergePromptPair[] = [];

  for (const [, group] of byKey) {
    if (group.length < 2) continue;
    // Stable ordering for pair-keys.
    const sorted = [...group].sort((a, b) => a.groupId.localeCompare(b.groupId));
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i];
        const b = sorted[j];
        const pairKey = `${a.groupId}|${b.groupId}`;
        currentPairKeys.add(pairKey);
        if (dismissedPairs.has(pairKey)) continue;
        if (lastSeenPairKeys.has(pairKey)) continue;
        // Pick which side is "edited" so the dialog can describe it from
        // the user's perspective — prefer the explicit lastEditedSaGroupId.
        const editedSaGroupId =
          lastEditedSaGroupId === a.groupId || lastEditedSaGroupId === b.groupId
            ? lastEditedSaGroupId
            : b.groupId;
        const matchedSaGroupId = editedSaGroupId === a.groupId ? b.groupId : a.groupId;
        const editedLabel = sorted.find(s => s.groupId === editedSaGroupId)?.addressLabel || '';
        const matchedLabel = sorted.find(s => s.groupId === matchedSaGroupId)?.addressLabel || '';
        newPairs.push({
          editedSaGroupId,
          matchedSaGroupId,
          pairKey,
          addressLabel: editedLabel || matchedLabel
        });
      }
    }
  }

  return { newPairs, currentPairKeys };
}

@Component({
  selector: 'app-bill-ai-flow-sa-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatCheckboxModule, SharedModule, EntityMatchSelectorComponent, ScoreSelectComponent, ParentContextBannerComponent, ParentConflictResolverComponent, MergeModalComponent, SelectionActionBarComponent],
  templateUrl: './bill-ai-flow-sa-step.component.html',
  styleUrl: './bill-ai-flow-sa-step.component.scss'
})
export class BillAiFlowSaStepComponent implements OnInit {
  stateOptions: string[] = [];

  // Track which SAs are expanded to show children/edit
  expandedSas = signal<Set<string>>(new Set());

  // Pre-computed values for template
  readonly state = inject(BillAiFlowStore);
  private matchingService = inject(MatchingService);
  private stateV4Svc = inject(StateV4Service);
  private snackBar = inject(MatSnackBar);
  legalEntities = computed(() => this.state.matchResponse()?.legalEntities ?? []);
  isNewCustomer = this.state.isNewCustomer;

  /**
   * SA groupIds that Express Import flagged as blocking fast-track on this
   * step. Template applies `.needs-attention` to these rows. Empty unless
   * all three hold: fast-track mode is 'fast', the SA step wasn't marked
   * skipped during execution, and the analyzer recorded blockers here.
   * See guides/ai-onboarding/FAST-TRACK.md.
   */
  fastTrackBlockingSet = computed<Set<string>>(() => {
    if (this.state.fastTrackMode() !== 'fast') return new Set();
    if (this.state.fastTrackSkippedSteps().includes('sa')) return new Set();
    const ids = this.state.fastTrackAnalysis()?.blockingGroupIds?.['sa'] ?? [];
    return new Set(ids);
  });

  // Address image loading state per SA groupId
  imageLoading = new Set<string>();

  // Image preview overlay
  previewImageUrl = signal<string | null>(null);

  // Entity mismatch warnings (pre-computed, keyed by groupId)
  lenMismatchExpected: Record<string, string> = {};
  private originalExtractedNames: Record<string, string> = {};

  // Inline editing state
  editingLenName: string | null = null;

  private imagesInitialized = false;

  // Pre-computed lookup maps for template (avoids method calls in templates)
  saEditDataMap: Record<string, SaEditData> = {};
  saAddressLinesMap: Record<string, string[]> = {};
  /** Individual address parts for inline display with missing-field highlighting */
  saAddressPartsMap: Record<string, { street1: string; street2: string; city: string; state: string; zip: string }> = {};
  saAddressMap: Record<string, string> = {};
  saChildSummaryMap: Record<string, string> = {};
  lenChildCountMap: Record<string, string> = {};
  originalNameMap: Record<string, string> = {};
  saForcedNewMap: Record<string, boolean> = {};
  saIsNewMap: Record<string, boolean> = {};
  /** Cross-entity duplicate per SA groupId — the row is fully blocked. */
  saBlockedMap: Record<string, boolean> = {};
  /** Blocked-note context: "LEN · address" where the duplicate address lives. */
  saExistsElsewhereMap: Record<string, string | null> = {};
  /** Exact in-scope address hit — linked to the existing address, Create New disabled. */
  saExactLockedMap: Record<string, boolean> = {};
  /** Any blocked row on this step — gates Continue (precomputed for the template). */
  anySaBlocked = false;
  expandedSaSet: Set<string> = new Set();
  imageLoadingSet: Set<string> = new Set();
  mergeSelectedMap: Record<string, boolean> = {};
  expandedBillMeters: Record<string, boolean> = {};
  previewingBillMap: Record<string, boolean> = {};
  billLabelMap: Record<string, string> = {};
  /**
   * Display string for each meter showing "label: value" pairs for the
   * mapped UANs, e.g. "ESIID: 12345 · Customer Number: 67890". Labels come
   * from the bill's utility_account_number mapping; falls back to generic
   * "UAN N" when the bill wasn't extracted with a canonical label.
   */
  meterUanDisplayMap: Record<string, string> = {};
  saScoreOptionsMap: Record<string, ScoreSelectOption[]> = {};
  saSelectionMap: Record<string, number | string | null> = {};

  // Duplicate SA detection (same-LEN info + cross-LEN warning)
  /** SA groupId → 'same-len-info' (will auto-merge) | 'cross-len-warning' (suspicious) | undefined */
  saDuplicateKindMap: Record<string, 'same-len-info' | 'cross-len-warning' | undefined> = {};
  /** Pre-computed message text per SA, matching saDuplicateKindMap. */
  saDuplicateMessageMap: Record<string, string> = {};

  sameLenDuplicates = this.state.sameLenDuplicateSas;
  crossLenDuplicates = this.state.crossLenDuplicateSas;
  hasSameLenDuplicates = this.state.hasSameLenDuplicateSas;
  hasCrossLenDuplicates = this.state.hasCrossLenDuplicateSas;
  hasSaStateMismatch = this.state.hasSaStateMismatch;

  // Merge modal state
  mergeSelection = this.state.saMergeSelection;
  mergeModalState: {
    isOpen: boolean;
    candidates: string[];
    labelMap: Record<string, string>;
    subLabelMap: Record<string, string>;
  } = {
    isOpen: false, candidates: [], labelMap: {}, subLabelMap: {}
  };
  canUndoMerge = this.state.canUndoSaMerge;
  canUndoDelete = this.state.canUndoSaDelete;

  // --- Validation state (see guides/ai-onboarding/FIELD-VALIDATION.md) ---
  fieldErrors: Record<string, Record<string, boolean>> = {};
  hasEntityErrors: Record<string, boolean> = {};
  validationSummary: ValidationSummary = { errorGroups: [], warningGroups: [], totalErrors: 0, totalWarnings: 0 };

  /**
   * Active prompt pair when an edit produced an exact-key duplicate. Null
   * when no prompt is open. Only one prompt at a time — if a second pair
   * appears mid-prompt the queued check will catch it after the first
   * resolves.
   */
  saMergePromptPair = signal<SaMergePromptPair | null>(null);
  /**
   * Pair keys the user has dismissed by clicking "Keep separate".
   * Stays in this set until the address is edited apart again (which
   * removes the pair from the active candidates and re-arms the prompt
   * for the next time it goes identical).
   */
  private dismissedDuplicatePairs = new Set<string>();
  /**
   * Last-seen pair-key set, used to distinguish "newly identical pair"
   * (open the prompt) from "pair has been identical for a while" (already
   * being handled). Without this we'd reopen the prompt on every keystroke
   * after the user dismisses it and continues editing other fields.
   */
  private lastSeenPairKeys = new Set<string>();
  /**
   * Tracks the SA groupId most recently touched by the user, so the
   * prompt can name the just-edited row as the merge target candidate
   * and tag it for inline guidance on dismiss.
   */
  private lastEditedSaGroupId: string | null = null;

  constructor() {
    this.stateV4Svc.getAllCountryStates().pipe(take(1)).subscribe({
      next: states => this.stateOptions = states.map(s => s.key),
      error: (err) => {
        console.error('[BillAiFlowSaStep] Failed to load state options:', err);
        // The State select is required for any new SA. Without options the
        // user can't satisfy validation but would have no idea why — surface
        // it instead of leaving the dropdown silently empty.
        this.snackBar.open(
          'Couldn\'t load the state list. Address edits may not validate — please refresh and try again.',
          'Dismiss',
          { duration: 8000 }
        );
      },
    });
    // React to match data becoming available
    effect(() => {
      const response = this.state.matchResponse();
      // Track saEdits so validation re-runs on field changes
      this.state.saEdits();
      if (response) {
        this.initImages();
        this.rebuildLookupMaps();
        this.revalidate();
      }
    });
    // Track which bill is currently being previewed
    effect(() => {
      const previewId = this.state.previewBillFileId();
      const mapping = this.state.billFileIdMapping();
      const map: Record<string, boolean> = {};
      for (const [groupId, fileId] of Object.entries(mapping)) {
        map[groupId] = fileId === previewId;
      }
      this.previewingBillMap = map;
    });
    // Track expanded SAs signal changes
    effect(() => {
      this.expandedSaSet = this.expandedSas();
    });
    // Track merge selection changes
    effect(() => {
      const selection = this.mergeSelection();
      const response = this.state.matchResponse();
      const selections = this.state.entitySelections();
      if (response) {
        for (const len of response.legalEntities) {
          for (const sa of len.serviceAddresses) {
            this.mergeSelectedMap[sa.groupId] = selection.includes(sa.groupId);
            this.saSelectionMap[sa.groupId] = selections[sa.groupId] ?? null;
          }
        }
      }
    });
    // Track same-LEN and cross-LEN duplicate SA changes
    effect(() => {
      const sameLen = this.state.sameLenDuplicateSas();
      const crossLen = this.state.crossLenDuplicateSas();
      const map: Record<string, 'same-len-info' | 'cross-len-warning' | undefined> = {};
      const messages: Record<string, string> = {};

      for (const group of sameLen) {
        for (const id of group.saGroupIds) {
          map[id] = 'same-len-info';
          messages[id] = 'This service address is identical to another and will be automatically merged on import.';
        }
      }
      // Cross-LEN dupes overwrite same-LEN if a SA somehow appears in both buckets.
      // (Shouldn't happen given how the store computes them, but defensive.)
      for (const group of crossLen) {
        for (const id of group.saGroupIds) {
          map[id] = 'cross-len-warning';
          messages[id] = 'This service address appears under multiple legal entities. Please confirm you didn\'t accidentally place it under two different LENs.';
        }
      }

      this.saDuplicateKindMap = map;
      this.saDuplicateMessageMap = messages;
    });
    // Detect identical-after-edit SA pairs and offer an auto-merge prompt.
    // Without this the user can pass through SA / Meter / Summary still
    // seeing two separate entities, even though import-time matching will
    // collapse them at the end. Tracking saMergePromptPair lets the next
    // queued pair surface the moment the current prompt is closed.
    effect(() => {
      this.state.saEdits();
      this.state.matchResponse();
      this.saMergePromptPair();
      this.recomputeMergePromptCandidates();
    });
  }

  ngOnInit(): void {
    this.initImages();
    this.computeMismatchWarnings();
    this.rebuildLookupMaps();
  }

  /** Rebuild all pre-computed lookup maps for template binding */
  private rebuildLookupMaps(): void {
    const response = this.state.matchResponse();
    if (!response) return;

    // Rebuilt from scratch — re-matches can clear a block.
    this.saBlockedMap = {};
    this.saExistsElsewhereMap = {};
    this.saExactLockedMap = {};
    this.anySaBlocked = false;

    for (const len of response.legalEntities) {
      this.lenChildCountMap[len.groupId] = this.getLenChildCount(len);
      this.originalNameMap[len.groupId] = this.state.getOriginalName(len.groupId);
      const isForcedNew = this.isSaForcedNew(len.groupId);
      this.saForcedNewMap[len.groupId] = isForcedNew;
      for (const sa of len.serviceAddresses) {
        this.saEditDataMap[sa.groupId] = this.state.getSaEdit(sa.groupId);
        this.saAddressLinesMap[sa.groupId] = this.getSaAddressLines(sa);
        const edit = this.saEditDataMap[sa.groupId];
        this.saAddressPartsMap[sa.groupId] = {
          street1: edit?.street1 ?? '',
          street2: edit?.street2 ?? '',
          city: edit?.city ?? '',
          state: edit?.state ?? '',
          zip: edit?.zip ?? '',
        };
        this.saAddressMap[sa.groupId] = this.getSaAddress(sa);
        this.saChildSummaryMap[sa.groupId] = this.getChildSummary(sa);
        this.saIsNewMap[`${len.groupId}__${sa.groupId}`] = this.isSaNew(len.groupId, sa.groupId);
        this.originalNameMap[sa.groupId] = this.state.getOriginalName(sa.groupId);
        // Cross-entity duplicate: address exists under a different LEN — blocked.
        const saBlocked = !!sa.crossParentMatch;
        this.saBlockedMap[sa.groupId] = saBlocked;
        if (saBlocked) {
          this.anySaBlocked = true;
          this.saExistsElsewhereMap[sa.groupId] =
            [sa.existingLenName?.trim(), sa.crossParentMatch?.label?.trim()].filter(Boolean).join(' · ') || null;
        }
        // Exact in-scope hit: linked; creating a duplicate address is never offered.
        this.saExactLockedMap[sa.groupId] = !saBlocked && !isForcedNew && sa.exactMatch === true;

        // Build score options for SA (filter out matches below threshold)
        const options: ScoreSelectOption[] = [];
        if (sa.match && sa.match.score >= MIN_DISPLAY_SCORE) {
          const pct = Math.round(sa.match.score * 100);
          options.push({
            value: sa.match.id,
            label: sa.match.label,
            score: pct,
            scoreClass: getScoreClass(pct)
          });
        }
        for (const alt of sa.alternatives || []) {
          if (alt.score < MIN_DISPLAY_SCORE) continue;
          if (!sa.match || alt.id !== sa.match.id) {
            const pct = Math.round(alt.score * 100);
            options.push({
              value: alt.id,
              label: alt.label,
              score: pct,
              scoreClass: getScoreClass(pct)
            });
          }
        }
        this.saScoreOptionsMap[sa.groupId] = options;

        for (const meter of sa.meters) {
          this.originalNameMap[meter.groupId] = this.state.getOriginalName(meter.groupId);
          // The UAN values for the meter live on the edit state (populated by
          // the backend extraction). Pair them with the best available label
          // — canonical from utility_account_number, else AI-extracted, else
          // a generic "UAN N" fallback.
          const edit = this.state.getMeterEdit(meter.groupId);
          const slot1Label = this.resolveSlotLabel(meter, edit.utilityAccountNumber1, 'utilityAccountNumberLabel');
          const slot2Label = this.resolveSlotLabel(meter, edit.utilityAccountNumber2, 'utilityAccountNumber2Label');
          const slot3Label = this.resolveSlotLabel(meter, edit.utilityAccountNumber3, 'utilityAccountNumber3Label');
          for (const bill of meter.bills) {
            const data = this.state.getBillData(bill.groupId);
            this.billLabelMap[bill.groupId] = formatBillPeriod(data?.billingPeriodStart, data?.billingPeriodEnd, bill.groupId);
          }
          this.meterUanDisplayMap[meter.groupId] = this.buildMeterUanDisplay(
            {
              utilityAccountNumber1: edit.utilityAccountNumber1 || undefined,
              utilityAccountNumber2: edit.utilityAccountNumber2 || undefined,
              utilityAccountNumber3: edit.utilityAccountNumber3 || undefined,
            },
            slot1Label,
            slot2Label,
            slot3Label,
            this.originalNameMap[meter.groupId]
          );
        }
      }
    }
  }

  /** Refresh a single SA's edit data (call after field changes) */
  refreshSaEditData(groupId: string): void {
    this.saEditDataMap[groupId] = this.state.getSaEdit(groupId);
  }

  /**
   * Resolve a label for a given UAN slot using, in order:
   *   1. Canonical utility_account_number label persisted on any of the
   *      meter's bills.
   *   2. AI-extracted verbatim name from additionalIdentifiers whose value
   *      matches the slot's current value.
   *   3. Undefined — callers fall back to a generic "UAN N" string.
   */
  private resolveSlotLabel(
    meter: { bills: Array<{ groupId: string }> },
    slotValue: string | undefined,
    canonicalKey: 'utilityAccountNumberLabel' | 'utilityAccountNumber2Label' | 'utilityAccountNumber3Label'
  ): string | undefined {
    for (const bill of meter.bills) {
      const data = this.state.getBillData(bill.groupId);
      const canonical = data?.[canonicalKey];
      if (canonical && canonical.trim()) {
        return canonical;
      }
      if (slotValue && data?.additionalIdentifiers) {
        const match = data.additionalIdentifiers.find(id => id.value === slotValue);
        if (match?.name) {
          return cleanIdentifierName(match.name);
        }
      }
    }
    return undefined;
  }

  /**
   * Produce a single display string for a meter showing "label: value" for
   * each filled UAN slot, joined by a middle dot. Uses canonical labels
   * from utility_account_number when available, falling back to "UAN 1" /
   * "UAN 2" / "UAN 3" via genericUanLabel. When no UAN1 is set we fall back
   * to the original extracted name so the meter is still identifiable — and
   * suppress UAN2/3 in that case, since a meter with no primary is in an
   * incomplete state and the secondary values would read as orphans next to
   * the fallback.
   */
  private buildMeterUanDisplay(
    meter: { utilityAccountNumber1?: string; utilityAccountNumber2?: string; utilityAccountNumber3?: string },
    slot1Label: string | undefined,
    slot2Label: string | undefined,
    slot3Label: string | undefined,
    originalName: string | undefined
  ): string {
    if (!meter.utilityAccountNumber1) {
      return originalName ? `Meter: ${originalName}` : '';
    }
    const parts: string[] = [`${slot1Label || genericUanLabel(1)}: ${meter.utilityAccountNumber1}`];
    if (meter.utilityAccountNumber2) {
      parts.push(`${slot2Label || genericUanLabel(2)}: ${meter.utilityAccountNumber2}`);
    }
    if (meter.utilityAccountNumber3) {
      parts.push(`${slot3Label || genericUanLabel(3)}: ${meter.utilityAccountNumber3}`);
    }
    return parts.join(' · ');
  }

  private computeMismatchWarnings(): void {
    const context = this.state.getContext();
    if (!context) return;
    const normalize = (s: string | undefined | null): string => (s || '').trim().toLowerCase();
    const request = this.state.matchRequest();
    if (!request) return;

    if ((context.level === 'SERVICE_ADDRESS' || context.level === 'METER') && context.lenName) {
      for (const len of request.legalEntities) {
        const extractedRaw = len.name || 'Unknown';
        this.originalExtractedNames[len.groupId] = extractedRaw;
        const extracted = normalize(extractedRaw);
        if (extracted && extracted !== normalize(context.lenName) && extracted !== 'unknown entity' && extracted !== 'unknown') {
          this.lenMismatchExpected[len.groupId] = context.lenName;
        }
      }
    }
  }

  getOriginalExtractedLenName(groupId: string): string {
    return this.originalExtractedNames[groupId] || 'Unknown';
  }

  useParentLenName(groupId: string): void {
    const expected = this.lenMismatchExpected[groupId];
    if (expected) {
      this.state.updateEntityName(groupId, expected);
      delete this.lenMismatchExpected[groupId];
    }
  }

  saveLenName(groupId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newName = input.value.trim();
    if (newName) {
      this.state.updateEntityName(groupId, newName);
      const context = this.state.getContext();
      if (context?.lenName) {
        const normalize = (s: string) => s.trim().toLowerCase();
        if (normalize(newName) === normalize(context.lenName)) {
          delete this.lenMismatchExpected[groupId];
        }
      }
    }
    this.editingLenName = null;
  }

  /** Load images for all SAs once match data is available */
  initImages(): void {
    if (this.imagesInitialized) return;
    this.imagesInitialized = true;

    const response = this.state.matchResponse();
    if (!response) return;

    for (const len of response.legalEntities) {
      for (const sa of len.serviceAddresses) {
        this.state.initSaEditFromRequest(sa.groupId);
        this.loadAddressImage(sa.groupId);
      }
    }
  }

  // --- Cascade Rules ---

  isSaForcedNew(lenGroupId: string): boolean {
    return this.isNewCustomer() || this.state.isNewEntity(lenGroupId);
  }

  isSaNew(lenGroupId: string, saGroupId: string): boolean {
    return this.isSaForcedNew(lenGroupId)
      || this.state.getSelection(saGroupId) === null;
  }

  // --- Expand/Collapse ---

  toggleExpand(lenGroupId: string, saGroupId: string): void {
    const current = new Set(this.expandedSas());
    if (current.has(saGroupId)) {
      current.delete(saGroupId);
    } else {
      current.add(saGroupId);
    }
    this.expandedSas.set(current);
  }

  isExpanded(groupId: string): boolean {
    return this.expandedSas().has(groupId);
  }

  // --- Match Selection ---

  onSelectionChange(groupId: string, value: number | string | null): void {
    this.state.updateSelection(groupId, value);
  }

  // --- SA Edit Handlers ---

  getSaEditData(groupId: string): SaEditData {
    return this.state.getSaEdit(groupId);
  }

  onSaFieldChange(groupId: string, field: keyof SaEditData, value: any): void {
    this.lastEditedSaGroupId = groupId;
    this.state.updateSaEdit(groupId, { [field]: value });
    this.refreshSaEditData(groupId);
    // State drives the utility list and the UAN validation rules. Clear
    // utility/rate-class on every meter under this SA so the meter step
    // re-derives them against the new state — otherwise the dropdown
    // shows stale options from the prior state.
    if (field === 'state') {
      const response = this.state.matchResponse();
      if (!response) return;
      for (const len of response.legalEntities) {
        for (const sa of len.serviceAddresses) {
          if (sa.groupId !== groupId) continue;
          for (const meter of sa.meters) {
            this.state.updateMeterEdit(meter.groupId, {
              utilityId: null,
              rateClassId: null,
              rateClassUserSet: false,
            });
          }
        }
      }
    }
  }

  /**
   * Build the input list for `detectNewIdenticalSaPairs`. SAs without a
   * street1+city are skipped — flagging them as "duplicates of each other
   * because both are blank" would be noise.
   */
  private buildSaPairInputs(): Array<{ groupId: string; addressKey: string; addressLabel: string }> {
    const result: Array<{ groupId: string; addressKey: string; addressLabel: string }> = [];
    const response = this.state.matchResponse();
    if (!response) return result;
    const edits = this.state.saEdits();
    for (const len of response.legalEntities) {
      for (const sa of len.serviceAddresses) {
        const edit = edits[sa.groupId];
        if (!edit?.street1 || !edit?.city) continue;
        const addressKey = buildAddressGroupingKey(edit.street1, edit.city, edit.state);
        result.push({
          groupId: sa.groupId,
          addressKey,
          addressLabel: this.saAddressMap[sa.groupId] || this.state.getOriginalName(sa.groupId)
        });
      }
    }
    return result;
  }

  /**
   * Detect newly-identical pairs and surface the prompt. lastSeenPairKeys
   * is only updated for the pair we actually surface — if several pairs
   * become identical on the same tick, queued ones stay "new" so they pop
   * in turn after the current prompt is resolved.
   */
  private recomputeMergePromptCandidates(): void {
    const { newPairs, currentPairKeys } = detectNewIdenticalSaPairs({
      saInputs: this.buildSaPairInputs(),
      dismissedPairs: this.dismissedDuplicatePairs,
      lastSeenPairKeys: this.lastSeenPairKeys,
      lastEditedSaGroupId: this.lastEditedSaGroupId
    });

    // Drop dismissals + last-seen entries for pairs that are no longer
    // identical so a fresh re-collision arms the prompt again.
    for (const key of [...this.lastSeenPairKeys]) {
      if (!currentPairKeys.has(key)) this.lastSeenPairKeys.delete(key);
    }
    for (const key of [...this.dismissedDuplicatePairs]) {
      if (!currentPairKeys.has(key)) this.dismissedDuplicatePairs.delete(key);
    }

    // Only open a prompt if nothing else is open. Mark only the surfaced
    // pair as seen; queued pairs stay in newPairs for the next tick.
    if (newPairs.length > 0 && this.saMergePromptPair() === null) {
      const next = newPairs[0];
      this.saMergePromptPair.set(next);
      this.lastSeenPairKeys.add(next.pairKey);
    }
  }

  /**
   * "Merge" — drives the existing checkbox-and-merge pipeline. Uses the
   * *matched* (pre-existing) SA as the target so its full address (including
   * zip) wins; the just-edited SA may only have had street/city/state changed
   * to produce the match, leaving its zip partial.
   *
   * Re-checks the pair against current state before calling mergeSas so a
   * stale prompt (e.g., the matched SA was deleted between open and click)
   * doesn't merge against a removed id, and only clears prompt state on a
   * successful merge so a failure leaves the user with a visible reason
   * instead of a silently-reopened prompt.
   */
  confirmAutoMerge(): void {
    const pair = this.saMergePromptPair();
    if (!pair) return;

    // Both SAs must still exist somewhere — collisions are commonly
    // cross-LEN, so don't require them to share a parent.
    const liveSaIds = new Set<string>();
    for (const len of this.state.matchResponse()?.legalEntities ?? []) {
      for (const sa of len.serviceAddresses) liveSaIds.add(sa.groupId);
    }
    const stillValid = liveSaIds.has(pair.editedSaGroupId) && liveSaIds.has(pair.matchedSaGroupId);
    if (!stillValid) {
      this.saMergePromptPair.set(null);
      this.dismissedDuplicatePairs.delete(pair.pairKey);
      this.lastSeenPairKeys.delete(pair.pairKey);
      return;
    }

    this.state.clearSaMergeSelection();
    this.state.toggleSaMergeSelection(pair.editedSaGroupId);
    this.state.toggleSaMergeSelection(pair.matchedSaGroupId);
    const ok = this.state.mergeSas(pair.matchedSaGroupId);
    if (!ok) {
      this.snackBar.open(
        'Couldn\'t merge those addresses. Please try again or use the Merge action.',
        'Dismiss',
        { duration: 6000 }
      );
      // Leave the prompt closed but mark the pair dismissed so we don't
      // reopen it in a loop on the next tick.
      this.dismissedDuplicatePairs.add(pair.pairKey);
      this.saMergePromptPair.set(null);
      return;
    }
    this.saMergePromptPair.set(null);
    // The merged pair's key drops out of currentPairKeys naturally and gets
    // pruned by the next recompute, so no manual cleanup needed.
    this.rebuildLookupMaps();
  }

  /**
   * Records the dismissal and closes the prompt. The next queued pair (if
   * any) surfaces on the next tick because the effect tracks saMergePromptPair.
   */
  dismissAutoMerge(): void {
    const pair = this.saMergePromptPair();
    if (!pair) return;
    this.dismissedDuplicatePairs.add(pair.pairKey);
    this.saMergePromptPair.set(null);
  }

  // --- Address Image Loading (Google Maps) ---

  async loadAddressImage(saGroupId: string): Promise<void> {
    const edit = this.state.getSaEdit(saGroupId);
    // If we already have an image, skip
    if (edit.verifiedImageUrl) return;

    const street = edit.street1 || '';
    const city = edit.city || '';
    const stateCode = edit.state || '';
    const zip = edit.zip || '';
    const addressString = `${street}, ${city}, ${stateCode} ${zip}`.trim();

    if (!street || !city || addressString.length < 10) {
      return;
    }

    this.imageLoading.add(saGroupId);

    try {
      const locationBias = { state: stateCode, city };
      const matches = await this.matchingService.findMatches(addressString, 'address', locationBias);

      if (matches.length > 0) {
        const bestMatch = matches[0];
        const details = await this.matchingService.getMatchDetails(bestMatch.id, 'address');

        let imageUrl: string | null = null;

        if (details?.lat && details?.lng) {
          imageUrl = await this.matchingService.getVisualForLocation(details.lat, details.lng, details.imageUrl);
          this.state.updateSaEdit(saGroupId, {
            verifiedImageUrl: imageUrl,
            lat: details.lat,
            lng: details.lng
          });
        } else if (details?.imageUrl) {
          this.state.updateSaEdit(saGroupId, { verifiedImageUrl: details.imageUrl });
        } else {
          this.state.updateSaEdit(saGroupId, {
            verifiedImageUrl: this.getStaticMapForAddress(addressString)
          });
        }

        // Check for auto-verification
        const current = addressString.toLowerCase().replace(/[^a-z0-9]/g, '');
        const suggested = (bestMatch.label + ' ' + (bestMatch.subLabel || '')).toLowerCase().replace(/[^a-z0-9]/g, '');
        if (current === suggested || bestMatch.score > 0.95) {
          this.state.updateSaEdit(saGroupId, { verified: true });
        }

        // Store suggested address details for verification modal
        this.suggestedAddresses.set(saGroupId, details);
      } else {
        this.state.updateSaEdit(saGroupId, {
          verifiedImageUrl: this.getStaticMapForAddress(addressString)
        });
      }
    } catch (err) {
      console.warn('[BillAiFlowSaStep] Address image loading failed for', saGroupId, '— falling back to static map:', err);
      this.state.updateSaEdit(saGroupId, {
        verifiedImageUrl: this.getStaticMapForAddress(addressString)
      });
    } finally {
      this.imageLoading.delete(saGroupId);
    }
  }

  private getStaticMapForAddress(address: string): string {
    const encodedAddress = encodeURIComponent(address);
    const apiKey = environment.googleMapApiKey;
    if (apiKey) {
      return `https://maps.googleapis.com/maps/api/staticmap?center=${encodedAddress}&zoom=17&size=400x400&maptype=roadmap&markers=color:red%7C${encodedAddress}&key=${apiKey}`;
    }
    return '';
  }

  isImageLoading(groupId: string): boolean {
    return this.imageLoading.has(groupId);
  }

  // --- Address Verification ---

  suggestedAddresses = new Map<string, any>();
  showVerificationModal = signal<string | null>(null);

  async verifyAddress(saGroupId: string): Promise<void> {
    const edit = this.state.getSaEdit(saGroupId);
    const street = edit.street1 || '';
    const city = edit.city || '';
    const stateCode = edit.state || '';
    const zip = edit.zip || '';
    const addressString = `${street}, ${city}, ${stateCode} ${zip}`;

    if (addressString.length < 10 || !street) return;

    const locationBias = { state: stateCode, city };
    const matches = await this.matchingService.findMatches(addressString, 'address', locationBias);

    if (matches.length > 0) {
      const bestMatch = matches[0];
      const details = await this.matchingService.getMatchDetails(bestMatch.id, 'address');
      this.suggestedAddresses.set(saGroupId, details);

      if (details?.lat && details?.lng) {
        const imageUrl = await this.matchingService.getVisualForLocation(details.lat, details.lng, details.imageUrl);
        this.state.updateSaEdit(saGroupId, {
          verifiedImageUrl: imageUrl,
          lat: details.lat,
          lng: details.lng
        });
      }

      // Check for discrepancy
      const current = addressString.toLowerCase().replace(/[^a-z0-9]/g, '');
      const suggested = (bestMatch.label + ' ' + (bestMatch.subLabel || '')).toLowerCase().replace(/[^a-z0-9]/g, '');

      if (current !== suggested && bestMatch.score < 0.98) {
        // Show verification modal
        this.showVerificationModal.set(saGroupId);
      } else {
        this.state.updateSaEdit(saGroupId, { verified: true });
      }
    }
  }

  confirmAddress(saGroupId: string, choice: 'extracted' | 'suggested'): void {
    if (choice === 'suggested') {
      const suggested = this.suggestedAddresses.get(saGroupId);
      if (suggested) {
        this.state.updateSaEdit(saGroupId, {
          street1: suggested.street || '',
          city: suggested.city || '',
          state: suggested.state || '',
          zip: suggested.zip || '',
          verified: true
        });
      }
    } else {
      this.state.updateSaEdit(saGroupId, { verified: true });
    }
    this.showVerificationModal.set(null);
  }

  cancelVerification(): void {
    this.showVerificationModal.set(null);
  }

  openImagePreview(url: string | null | undefined): void {
    if (url) {
      this.previewImageUrl.set(url);
    }
  }

  closeImagePreview(): void {
    this.previewImageUrl.set(null);
  }

  previewBill(meter: MeterMatchResult) {
    if (meter.bills && meter.bills.length > 0) {
      if (meter.bills.length === 1) {
        this.previewSpecificBill(meter.bills[0]);
      } else {
        this.toggleBillExpansion(meter.groupId);
      }
    }
  }

  toggleBillExpansion(meterGroupId: string): void {
    this.expandedBillMeters[meterGroupId] = !this.expandedBillMeters[meterGroupId];
  }

  previewSpecificBill(bill: BillMatchResult): void {
    const billFile = this.state.getBillFileForGroup(bill.groupId);
    if (billFile) {
      this.state.setPreviewBillFile(billFile);
    }
  }

  getBillLabel(bill: BillMatchResult): string {
    if (this.billLabelMap[bill.groupId]) return this.billLabelMap[bill.groupId];
    const data = this.state.getBillData(bill.groupId);
    const label = formatBillPeriod(data?.billingPeriodStart, data?.billingPeriodEnd, bill.groupId);
    this.billLabelMap[bill.groupId] = label;
    return label;
  }
  
  getSuggestedAddress(saGroupId: string): any {
    return this.suggestedAddresses.get(saGroupId) || null;
  }

  // --- Children Counts ---

  getChildSummary(sa: ServiceAddressMatchResult): string {
    let meterCount = 0;
    let billCount = 0;
    for (const meter of sa.meters) {
      meterCount++;
      billCount += meter.bills.length;
    }
    const parts: string[] = [];
    parts.push(`${meterCount} meter${meterCount !== 1 ? 's' : ''}`);
    parts.push(`${billCount} bill${billCount !== 1 ? 's' : ''}`);
    return parts.join(', ');
  }

  getSaAddress(sa: ServiceAddressMatchResult): string {
    const edit = this.getSaEditData(sa.groupId);
    const parts = [edit.street1, edit.street2, edit.city, edit.state, edit.zip].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(', ');
    }
    return this.state.getOriginalName(sa.groupId);
  }

  getSaAddressLines(sa: ServiceAddressMatchResult): string[] {
    const edit = this.getSaEditData(sa.groupId);
    const lines = [];
    if (edit.street1) {
      lines.push(edit.street1 + (edit.street2 ? ', ' + edit.street2 : ''));
    }
    if (edit.city || edit.state || edit.zip) {
      const line2 = [edit.city, edit.state, edit.zip].filter(Boolean).join(', ');
      if (line2) lines.push(line2);
    }
    if (lines.length > 0) return lines;
    
    // Fallback
    const orig = this.state.getOriginalName(sa.groupId);
    return orig.split(',').map(s => s.trim());
  }

  getLenChildCount(len: LegalEntityMatchResult): string {
    const saCount = len.serviceAddresses.length;
    let meterCount = 0;
    let billCount = 0;
    for (const sa of len.serviceAddresses) {
      for (const meter of sa.meters) {
        meterCount++;
        billCount += meter.bills.length;
      }
    }
    return `${saCount} address${saCount !== 1 ? 'es' : ''}, ${meterCount} meter${meterCount !== 1 ? 's' : ''}, ${billCount} bill${billCount !== 1 ? 's' : ''}`;
  }

  // --- Merge ---

  toggleMergeSelection(groupId: string): void {
    this.state.toggleSaMergeSelection(groupId);
  }

  /** Total SA count across all LENs (computed signal for template perf) */
  totalSaCount = computed(() => {
    let count = 0;
    for (const len of this.legalEntities()) {
      count += len.serviceAddresses.length;
    }
    return count;
  });

  openMergeModal(): void {
    const mergeIds = this.mergeSelection();
    if (mergeIds.length < 2) return;

    const labelMap: Record<string, string> = {};
    const subLabelMap: Record<string, string> = {};
    // Map each candidate SA to its parent LEN's display name so the
    // user can see the hierarchy below the address — important when
    // merging across legal entities.
    const response = this.state.matchResponse();
    if (response) {
      for (const len of response.legalEntities) {
        const lenName = this.state.getOriginalName(len.groupId);
        for (const sa of len.serviceAddresses) {
          if (mergeIds.includes(sa.groupId)) {
            subLabelMap[sa.groupId] = lenName;
          }
        }
      }
    }
    for (const groupId of mergeIds) {
      labelMap[groupId] = this.saAddressMap[groupId] || this.state.getOriginalName(groupId);
    }

    this.mergeModalState = { isOpen: true, candidates: [...mergeIds], labelMap, subLabelMap };
  }

  closeMergeModal(): void {
    this.mergeModalState = { ...this.mergeModalState, isOpen: false };
  }

  onMergeConfirmed(targetGroupId: string): void {
    this.closeMergeModal();
    const ok = this.state.mergeSas(targetGroupId);
    if (!ok) {
      this.snackBar.open(
        'Couldn\'t merge those addresses. Please try again.',
        'Dismiss',
        { duration: 6000 }
      );
      return;
    }
    this.rebuildLookupMaps();
  }

  undoMerge(): void {
    this.state.undoLastSaMerge();
    this.rebuildLookupMaps();
  }

  // Last-item delete confirmation
  showLastItemConfirm = false;

  deleteSelected(): void {
    if (this.state.wouldDeleteAll('sa')) {
      this.showLastItemConfirm = true;
      return;
    }
    this.state.deleteSas();
    this.rebuildLookupMaps();
  }

  confirmLastItemDelete(): void {
    this.showLastItemConfirm = false;
    this.state.requestClose();
  }

  cancelLastItemDelete(): void {
    this.showLastItemConfirm = false;
  }

  undoDelete(): void {
    this.state.undoLastSaDelete();
    this.rebuildLookupMaps();
  }

  /** Pre-select duplicate SAs for merge (one-click from the warning banner) */
  selectDuplicatesForMerge(saGroupIds: string[]): void {
    this.state.clearSaMergeSelection();
    for (const id of saGroupIds) {
      this.state.toggleSaMergeSelection(id);
    }
  }

  // --- Navigation ---

  goBack(): void {
    this.state.goBack();
  }

  async goForward(): Promise<void> {
    // Cross-entity duplicates block the step — resolve via migrate/edit/abort.
    if (this.anySaBlocked) return;

    if (this.state.isMatching()) return; // re-entry guard: a re-match is already in flight
    if (this.hasSaStateMismatch()) return; // hard-stop: state can't be changed on existing SA
    const result = this.runValidation();
    this.rebuildFieldErrors(result);
    if (result.hasBlockers) return;

    // Committed selections narrow downstream candidate pools — re-fire the
    // match before the analyzer so cascade + pause banners on the meter
    // step reflect the new scope.
    if (this.state.needsRematch()) {
      await this.state.runMatch(Number(this.state.selectedCustomerId()), { reuseRequest: true });
      if (this.state.error()) return; // stay put — navigating would clear the error banner
    }
    this.state.completeStep('sa');
    this.state.analyzeFastTrack();
    const next = this.state.getNextStep();
    if (next) {
      this.state.goToStep(next);
    }
  }

  private runValidation(): StepValidationResult {
    const input: SaValidationInput = {
      legalEntities: this.state.matchResponse()?.legalEntities ?? [],
      saEdits: this.state.saEdits(),
      isNewEntity: (gid) => this.state.isNewEntity(gid),
    };
    return validateSaStep(input);
  }

  private rebuildFieldErrors(result: StepValidationResult): void {
    const maps = buildFieldErrorMaps(result);
    this.fieldErrors = maps.byField;
    this.hasEntityErrors = maps.byEntity;
    this.validationSummary = maps.summary;
  }

  revalidate(): void {
    this.rebuildFieldErrors(this.runValidation());
  }

  onMissingFieldClick(saGroupId: string, field: string): void {
    // Expand the edit panel if collapsed
    const expanded = new Set(this.expandedSas());
    expanded.add(saGroupId);
    this.expandedSas.set(expanded);

    // Focus the input on next tick
    queueMicrotask(() => {
      const el = document.getElementById(`sa-${saGroupId}-${field}`);
      if (el) (el as HTMLElement).focus();
    });
  }
}
